import { Collection, getFirebase } from 'firestorter';
import chunk from 'lodash.chunk';

import Collections from '../utils/collections';
import BaseDocument from './BaseDocument';

const Section = {
  CHATS: 'chats',
  CHECKINS: 'checkins',
  WORKOUTS: 'workouts',
  MEAL_PLANS: 'mealPlans',
  VIDEO_FEEDBACK: 'videoFeedback',
  REMINDERS: 'reminders',
  ROUTING: 'routing',
};

const CLAIM_FIELDS = ['claimedBy', 'claimedByUsername', 'claimedAt'];

const DOCUMENT_ID_QUERY_LIMIT = 10;

const buildClaimData = (claimedBy, claimedByUsername) => ({
  claimedBy,
  claimedByUsername,
  claimedAt: new Date(),
});

const buildClaimFieldUpdate = (claimedSection, getValue) => CLAIM_FIELDS.reduce((updateData, field) => ({
  ...updateData,
  [`${claimedSection}.${field}`]: getValue(field),
}), {});

const buildClaimUpdate = (claimedSection, claimData) => {
  const updateData = buildClaimFieldUpdate(claimedSection, (field) => claimData[field]);

  // Routing has no server-side projection that clears it (the other sections' counts are
  // recomputed by Firestore triggers from source collections). Claiming a routing flag IS
  // the resolution, so zero its count here. Doing this for any other section would just be
  // overwritten by its trigger.
  if (claimedSection === Section.ROUTING) {
    const { FieldValue } = getFirebase().firestore;

    updateData[`${claimedSection}.unreadCount`] = 0;
    // The wait ends with the count, so drop where it started too - the chats projection does the
    // same when its last unread clears. Left behind, a zeroed section would still report a weeks
    // old wait to anything reading the document outside the dashboard.
    updateData[`${claimedSection}.oldestNotificationAt`] = FieldValue.delete();
  }

  return updateData;
};

const runClaimTransaction = (ref, claimedSection, claimedBy, claimedByUsername, allowTakeover) => {
  const db = getFirebase().default.firestore();

  // Run the update in a transaction to ensure that the section is not claimed by someone else in the meantime.
  return db.runTransaction(async (transaction) => {
    const doc = await transaction.get(ref);

    if (!doc.exists) {
      return null;
    }

    const currentClaim = doc.data()[claimedSection] ?? {};

    if (!allowTakeover && currentClaim.claimedBy && currentClaim.claimedBy !== claimedBy) {
      return currentClaim;
    }

    const newClaimData = buildClaimData(claimedBy, claimedByUsername);
    transaction.update(ref, buildClaimUpdate(claimedSection, newClaimData));
    return newClaimData;
  });
};

/**
 * How often the server rebuilds the derived counts on these documents, in minutes.
 *
 * `overdueCount` and the per-pod counters have no trigger that could maintain them — an item
 * crosses its SLA through the passage of time, and nothing is written at that moment — so they are
 * recomputed wholesale on a schedule by `recomputeOverdueCounts` (system2-server
 * `functions-22/src/coachNotificationView`). They are therefore accurate as of its last run and no
 * fresher, while a section's own `unreadCount` and every item age are live.
 *
 * Mirrored here so the dashboards can say which of the two numbers they are showing is live. It
 * must match the function's `schedule`, or the page would promise a freshness it does not have.
 */
const COUNTS_RECOMPUTE_INTERVAL_MINUTES = 30;

/**
 * How long the oldest pending item of a section may wait before the AC has breached its SLA,
 * expressed in business-day hours (weekends excluded — see getElapsedBusinessHours).
 *
 * The server counts overdue items against the same table, so the two must agree or an item would be
 * counted past due there and shown as within SLA here — see `SECTION_SLA_BUSINESS_HOURS` in
 * system2-server `functions-22/src/coachNotificationView/constants.ts`, which is now the only other
 * copy.
 *
 * Routing is the one entry with no server counterpart. It is a coach-level flag rather than a queue
 * of items, so the server has nothing to count; its breach is worked out here from `lastFlaggedAt`.
 */
const SECTION_SLA_BUSINESS_HOURS = {
  [Section.CHATS]: 24,
  [Section.CHECKINS]: 48,
  [Section.WORKOUTS]: 24,
  [Section.MEAL_PLANS]: 24,
  [Section.VIDEO_FEEDBACK]: 24,
  [Section.REMINDERS]: 24,
  [Section.ROUTING]: 24,
};

/**
 * class representing the notifications view data of a coach
 *
 * @class CoachNotificationView
 * @extends BaseDocument
 */
class CoachNotificationView extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.COACH_NOTIFICATION_VIEW}/${id}`, opts);
  }

  /**
   * Get the coach name
   * @return {string}
   */
  get name() {
    return this.data.name;
  }

  /**
   * Get coach's chats section data
   * @return {object}
   */
  get chats() {
    return this.data.chats;
  }

  /**
   * Get coach's checkins section data
   * @return {object}
   */
  get checkins() {
    return this.data.checkins;
  }

  /**
   * Get coach's workouts section data
   * @return {object}
   */
  get workouts() {
    return this.data.workouts;
  }

  /**
   * Get coach's mealPlans section data
   * @return {object}
   */
  get mealPlans() {
    return this.data.mealPlans;
  }

  /**
   * Get coach's videoFeedback section data
   * @return {object}
   */
  get videoFeedback() {
    return this.data.videoFeedback;
  }

  /**
   * Get coach's reminders section data
   * @return {object}
   */
  get reminders() {
    return this.data.reminders;
  }

  /**
   * Get coach's routing section data
   * @return {object}
   */
  get routing() {
    return this.data.routing;
  }

  /**
   * Claims a section for a user, unless another user already holds it. A claim never expires -
   * it ends when its holder releases it, or when another user takes it over via reclaim.
   *
   * @param {string} claimedSection - The section that is being claimed.
   * @param {string} claimedBy - Id of the user claiming the section.
   * @param {string} claimedByUsername - Username of the user claiming the section.
   * @returns {Promise<Object|null>} The resulting claim data - the new claim when it was granted,
   * the standing claim of the user who already holds the section otherwise, and null when the
   * document no longer exists.
   */
  async claim(claimedSection, claimedBy, claimedByUsername) {
    return runClaimTransaction(this.ref, claimedSection, claimedBy, claimedByUsername, false);
  }

  /**
   * Takes a section over, overwriting the claim of whoever currently holds it. Used when a user
   * explicitly confirms the take-over.
   *
   * @param {string} claimedSection - The section that is being taken over.
   * @param {string} claimedBy - Id of the user taking the section over.
   * @param {string} claimedByUsername - Username of the user taking the section over.
   * @returns {Promise<Object|null>} The new claim data, or null when the document no longer exists.
   */
  async reclaim(claimedSection, claimedBy, claimedByUsername) {
    return runClaimTransaction(this.ref, claimedSection, claimedBy, claimedByUsername, true);
  }

  /**
   * Hands a section back to the pool by clearing its claim. Only the user holding the claim can
   * release it, so an AC cannot release another AC's work.
   *
   * @param {string} claimedSection - The section that is being released.
   * @param {string} claimedBy - Id of the user releasing the section.
   * @returns {Promise<boolean>} True when the claim was cleared, false when the document no longer
   * exists or the section is not held by the given user.
   */
  async unclaim(claimedSection, claimedBy) {
    const db = getFirebase().default.firestore();

    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);

      if (!doc.exists || doc.data()[claimedSection]?.claimedBy !== claimedBy) {
        return false;
      }

      const { FieldValue } = getFirebase().firestore;

      transaction.update(this.ref, buildClaimFieldUpdate(claimedSection, () => FieldValue.delete()));
      return true;
    });
  }

  /**
   * Get the notification views of specific coaches, whatever their AC enrollment. Pool membership
   * is an explicit AC Ops decision, so a pooled coach has to stay visible in the queue even when
   * the flags behind getAllExternalACCoachNotificationViews would exclude them - otherwise their
   * outstanding work is invisible to every AC with no error anywhere.
   *
   * A document id query takes at most DOCUMENT_ID_QUERY_LIMIT ids on this SDK version, so the
   * pool is fetched in chunks. The collections are returned rather than their documents so the
   * caller keeps them observed.
   *
   * @param {string[]} coachIds - Ids of the coaches to fetch.
   * @return {Promise<Array<Collection>>} The collections holding the matching view documents.
   */
  static async getByCoachIds(coachIds) {
    return Promise.all(chunk(coachIds, DOCUMENT_ID_QUERY_LIMIT).map((coachIdsChunk) => (
      CoachNotificationView.createViewCollection(
        (query) => query.where(getFirebase().firestore.FieldPath.documentId(), 'in', coachIdsChunk),
      )
    )));
  }

  /**
   * Get all external and active AC enabled coaches' notification views.
   * @return {Promise<Collection>} Collection of view documents.
   */
  static async getAllExternalACCoachNotificationViews() {
    return CoachNotificationView.createViewCollection((query) => query
      .where('isS2Coach', '==', false)
      .where('isActive', '==', true)
      .where('isACEnabled', '==', true));
  }

  /**
   * Builds a collection of view documents for a query and fetches it. The collection is returned
   * rather than its documents so the caller keeps it observed.
   *
   * @param {function} query - Builds the firestore query for the collection.
   * @return {Promise<Collection>} The fetched collection.
   */
  static async createViewCollection(query) {
    const collection = new Collection(Collections.COACH_NOTIFICATION_VIEW, {
      createDocument: ({ id }, opts) => new CoachNotificationView(id, { ...opts, disableObserverRefCount: true }),
      query,
    });
    await collection.fetch();
    return collection;
  }
}

export default CoachNotificationView;
export { COUNTS_RECOMPUTE_INTERVAL_MINUTES, Section, SECTION_SLA_BUSINESS_HOURS };
